/**
 * The person World has signed in on this origin (ADR-224 §4.3; Allen 2026-10-03: "the manual path
 * returns too"). World and Harry share poqpoq.com, and World keeps `auth_user` (`{id, name, …}`) and
 * `auth_token` (the suite JWT) in sessionStorage and localStorage. A session that did not start from
 * World's upload stamps its hand-off record with this id, so World can offer the fixed file back.
 *
 * DECODE ONLY, NEVER TRUST. Harry cannot verify a token (no secret) and never sends one here: the id
 * names WHOSE World tab may pick the record up, and World compares it with its own signed-in person.
 * World's pairing rule is kept: `auth_user.id` must equal the token's `user_id` claim, else nobody.
 * sessionStorage is read first, then localStorage (World's `migrateToSessionStorage`).
 *
 * Depends on: injected storages. Depended on by: main.ts (the session's `worldUser`), tests.
 */

export interface StorageLike {
  getItem(key: string): string | null;
}

export interface WorldUser {
  readonly id: string;
  readonly name: string | null;
}

/** The `user_id` claim of a JWT's payload, unverified; null when it is not a readable JWT. */
export function tokenUserId(token: string): string | null {
  const part = token.split('.')[1];
  if (part === undefined) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const claims = JSON.parse(json) as { user_id?: unknown; sub?: unknown };
    const id = claims.user_id ?? claims.sub;
    return typeof id === 'string' && id !== '' ? id : null;
  } catch {
    return null;
  }
}

function readFrom(storage: StorageLike): WorldUser | null {
  const rawUser = storage.getItem('auth_user');
  const token = storage.getItem('auth_token');
  if (rawUser === null || token === null) return null;
  let user: { id?: unknown; name?: unknown };
  try {
    user = JSON.parse(rawUser) as typeof user;
  } catch {
    return null;
  }
  if (typeof user.id !== 'string' || user.id === '') return null;
  // World's pairing rule (codex #1653): the user and the token must name the same person.
  if (tokenUserId(token) !== user.id) return null;
  return { id: user.id, name: typeof user.name === 'string' && user.name !== '' ? user.name : null };
}

/** The signed-in World person, or null. Each storage can throw (a sandboxed frame, blocked storage): that is nobody. */
export function readWorldUser(storages: { readonly session: () => StorageLike | null; readonly local: () => StorageLike | null }): WorldUser | null {
  for (const get of [storages.session, storages.local]) {
    try {
      const s = get();
      const user = s === null ? null : readFrom(s);
      if (user !== null) return user;
    } catch {
      /* this storage refused; try the next — the caller falls back to "save it and upload it" */
    }
  }
  return null;
}
